/**
 * FTE Calculator
 *
 * Calculates required Full-Time Equivalents (FTE) based on
 * target utilization and expected workload.
 */

import {
  ScenarioInputs,
  ScenarioResults,
  MonthlyProjection,
  FTE_CONSTANTS,
} from '../types/scenario.types';
import { CapacitySnapshot } from '../types/capacity.types';
import { mockCurrentFTE } from '../mocks/mockCapacityData';

/**
 * Calculate required FTE for a given workload and target utilization
 *
 * Formula: Required FTE = Total Visit Minutes / (Target Utilization × Minutes per FTE)
 *
 * @param totalVisitMinutes - Total visit time required per month
 * @param targetUtilization - Target utilization (0.0 - 1.0)
 * @returns Required FTE
 */
export function calculateRequiredFTE(
  totalVisitMinutes: number,
  targetUtilization: number
): number {
  if (targetUtilization <= 0 || targetUtilization > 1) {
    throw new Error('Target utilization must be between 0 and 1');
  }

  const requiredFTE = totalVisitMinutes / (targetUtilization * FTE_CONSTANTS.MINUTES_PER_MONTH);
  return Math.ceil(requiredFTE * 10) / 10; // Round to 1 decimal place
}

/**
 * Calculate current FTE from capacity snapshot
 *
 * Estimates current FTE based on shift data over a period
 *
 * @param snapshot - Capacity snapshot
 * @returns Current FTE by role
 */
export function calculateCurrentFTE(snapshot: CapacitySnapshot): { [role: string]: number } {
  const roleShiftMinutes: { [role: string]: number } = {};
  const roleDays: { [role: string]: number } = {};

  // Sum shift minutes by role
  snapshot.providers.forEach((day) => {
    if (!roleShiftMinutes[day.role]) {
      roleShiftMinutes[day.role] = 0;
      roleDays[day.role] = 0;
    }
    roleShiftMinutes[day.role] += day.shiftMinutes;
    roleDays[day.role]++;
  });

  // Calculate FTE: (total shift minutes / days) * 30 days/month / minutes per FTE per month
  const currentFTE: { [role: string]: number } = {};
  Object.keys(roleShiftMinutes).forEach((role) => {
    const avgShiftMinutesPerDay = roleShiftMinutes[role] / roleDays[role];
    const monthlyShiftMinutes = avgShiftMinutesPerDay * 30; // Assume 30 days/month
    currentFTE[role] = Math.ceil((monthlyShiftMinutes / FTE_CONSTANTS.MINUTES_PER_MONTH) * 10) / 10;
  });

  return currentFTE;
}

/**
 * Run scenario modeling calculations
 *
 * @param inputs - Scenario inputs
 * @param snapshot - Current capacity snapshot
 * @returns Scenario results with FTE recommendations
 */
export function runScenarioModel(
  inputs: ScenarioInputs,
  snapshot: CapacitySnapshot
): ScenarioResults {
  const {
    targetUtilization,
    monthlyPatientVolume,
    visitsPerPatient,
    growthRatePercent,
    planningMonths,
  } = inputs;

  // Use mock current FTE data
  const currentFTE = mockCurrentFTE;

  // Calculate required visit minutes by role
  const requiredVisitMinutesByRole: { [role: string]: number } = {};
  Object.keys(visitsPerPatient).forEach((role) => {
    const visitsPerMonth = monthlyPatientVolume * visitsPerPatient[role];
    // Assume average visit is 60 minutes
    const visitMinutes = visitsPerMonth * 60;
    requiredVisitMinutesByRole[role] = visitMinutes;
  });

  // Calculate required FTE by role
  const requiredFTE: { [role: string]: number } = {};
  Object.keys(requiredVisitMinutesByRole).forEach((role) => {
    requiredFTE[role] = calculateRequiredFTE(
      requiredVisitMinutesByRole[role],
      targetUtilization
    );
  });

  // Calculate delta (required - current)
  const delta: { [role: string]: number } = {};
  Object.keys(requiredFTE).forEach((role) => {
    delta[role] = Math.round((requiredFTE[role] - (currentFTE[role] || 0)) * 10) / 10;
  });

  // Generate monthly projections with growth
  const projectedMonthly: MonthlyProjection[] = [];
  const growthRate = growthRatePercent / 100;

  for (let month = 0; month < planningMonths; month++) {
    const growthMultiplier = Math.pow(1 + growthRate, month);
    const projectedVolume = Math.round(monthlyPatientVolume * growthMultiplier);

    const monthRequiredFTE: { [role: string]: number } = {};
    Object.keys(requiredVisitMinutesByRole).forEach((role) => {
      const projectedMinutes = requiredVisitMinutesByRole[role] * growthMultiplier;
      monthRequiredFTE[role] = calculateRequiredFTE(projectedMinutes, targetUtilization);
    });

    projectedMonthly.push({
      month,
      monthLabel: getMonthLabel(month),
      patientVolume: projectedVolume,
      requiredFTE: monthRequiredFTE,
    });
  }

  return {
    requiredFTE,
    currentFTE,
    delta,
    projectedMonthly,
  };
}

/**
 * Get month label for display
 */
function getMonthLabel(monthOffset: number): string {
  const now = new Date();
  const targetDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  return targetDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
}

/**
 * Get default scenario inputs
 */
export function getDefaultScenarioInputs(): ScenarioInputs {
  return {
    targetUtilization: 0.75, // 75%
    monthlyPatientVolume: 85,
    visitsPerPatient: {
      RN: 7,
      PT: 6,
      OT: 4,
    },
    growthRatePercent: 5,
    planningMonths: 12,
  };
}
